/**
 * Start a session from an idea: prompt + project + machine + agent → signed `run.start` with a
 * client-minted runId (so a retry can't start the run twice), then open the live session.
 */
import { useNavigate } from '@tanstack/react-router'
import {
  AgentGlyph,
  effortsFor,
  Kbd,
  pickerModels,
  shortcutProps,
  useShortcut,
} from '@viko/transcript-ui'
import { AlertTriangle, Laptop, Monitor, Play } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { autoAccount } from '../lib/account-usage.js'
import { ApiError, StorageNotConfigured } from '../lib/api.js'
import { LARGE_TEXT_BYTES, useAttachmentInput } from '../lib/attachment-input.js'
import { CommandRejected, useBackend } from '../lib/backend.js'
import { newSessionAttachmentScope } from '../lib/encrypted-artifacts.js'
import { CommandTimeoutError } from '../lib/hub.js'
import { runHref } from '../lib/ia.js' // ia-shell
import { accountStatus, accountUsable } from '../lib/provider-accounts.js'
import { useMachines, useProjects } from '../lib/queries.js'
import { suggestRoute, useRoutingOutcomes, useRoutingPreferences } from '../lib/routing-prefs.js'
import {
  START_REPLY_MS,
  type StartFailure,
  StartNoReply,
  startFailure,
} from '../lib/start-failure.js'
import { AttachmentDropZone } from './AttachmentDropZone.js'
import type { StartPrefill } from './app-ui.js'
import { storage } from './Capture.js'
import { DialogFooter } from './DialogFooter.js'
import { Button, errorText, Sheet, useToast } from './ui.js'

const AUTO_ACCOUNT = '__auto__'

export function StartSession({
  open,
  prefill,
  onClose,
}: {
  open: boolean
  prefill?: StartPrefill
  onClose: () => void
}) {
  const b = useBackend()
  const toast = useToast()
  const nav = useNavigate()
  const projects = useProjects()
  const machines = useMachines()
  const [prompt, setPrompt] = useState('')
  const [projectId, setProjectId] = useState<string | undefined>()
  const [machineId, setMachineId] = useState<string | undefined>()
  const [agent, setAgent] = useState<'claude' | 'codex'>('claude')
  const [selection, setSelection] = useState<{ machineId: string; agent: string; id: string }>()
  const [model, setModel] = useState('')
  const [effort, setEffort] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<StartFailure>()
  const input = useAttachmentInput({
    storageOff: storage.off,
    largeTextBytes: LARGE_TEXT_BYTES,
    insertText: (t) => setPrompt((cur) => cur + t),
  })

  useEffect(() => {
    if (!open) return
    setPrompt(prefill?.prompt ?? '')
    input.clear()
    setProjectId(prefill?.projectId ?? projects.data?.[0]?.id)
    setFailure(undefined)
  }, [open, prefill, projects.data, input.clear])

  const project = projects.data?.find((p) => p.id === projectId)
  // account-projects: a project runs only on machines that report a checkout of it.
  const allowed = useMemo(() => {
    const all = machines.data ?? []
    return project ? all.filter((m) => project.machines.includes(m.id)) : all
  }, [machines.data, project])
  const noCheckout = !!project && machines.isSuccess && allowed.length === 0
  useEffect(() => {
    if (!open) return
    if (prefill?.machineId && allowed.some((m) => m.id === prefill.machineId))
      return setMachineId(prefill.machineId)
    if (!allowed.some((m) => m.id === machineId)) setMachineId(allowed.find((m) => m.online)?.id)
  }, [allowed, machineId, open, prefill?.machineId])

  // G11 "Auto": stated rules > learned evidence > plan defaults (image generation, goal loops → Codex).
  const routingPrefs = useRoutingPreferences()
  const routingOutcomes = useRoutingOutcomes()
  const [autoAgent, setAutoAgent] = useState(true)
  const autoRoute = useMemo(
    () =>
      suggestRoute(prompt, projectId, machines.data ?? [], {
        prefs: routingPrefs.data,
        outcomes: routingOutcomes.data,
      }),
    [prompt, projectId, machines.data, routingPrefs.data, routingOutcomes.data],
  )
  useEffect(() => {
    if (autoAgent && autoRoute?.agent) setAgent(autoRoute.agent)
  }, [autoAgent, autoRoute?.agent])

  // Models belong to an agent: switching agent returns to its default.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset on agent change only
  useEffect(() => {
    setModel('')
    setEffort('')
  }, [agent])

  const machine = allowed.find((m) => m.id === machineId)
  const accounts = machine?.providerAccounts?.filter((a) => a.provider === agent)
  const scopedSelection =
    selection && selection.machineId === machineId && selection.agent === agent
      ? selection.id
      : undefined
  // An explicit choice that becomes missing/disabled must never silently change identity.
  // claude-accounts: "Auto" runs the reset-aware router and sends its pick as an explicit account.
  const auto =
    scopedSelection === AUTO_ACCOUNT && machine
      ? autoAccount(machine, agent, machines.data ?? [], Date.now())
      : undefined
  const accountId =
    scopedSelection === AUTO_ACCOUNT
      ? auto?.ok
        ? auto.localId
        : undefined
      : (scopedSelection ?? accounts?.find((a) => a.id === `${agent}:default`)?.id)
  const account = accounts?.find((a) => a.id === accountId)
  const accountBlocked =
    Boolean(scopedSelection && !accounts) ||
    (accounts !== undefined && (!account || !accountUsable(account)))
  const canAuto = (accounts?.filter((a) => a.enabled).length ?? 0) > 1

  // Dialog keys: ⌘/Ctrl+Enter starts; ⌥P/⇧⌥P cycle project, ⌥A agent, ⌥M focuses the model.
  const cycle = <T,>(items: T[], cur: T | undefined, step: 1 | -1): T | undefined =>
    items.length
      ? items[(Math.max(0, items.indexOf(cur as T)) + step + items.length) % items.length]
      : undefined
  const on = open
  useShortcut('start.submit', () => void start(), on)
  useShortcut(
    'start.project',
    () =>
      setProjectId(
        cycle(
          (projects.data ?? []).map((p) => p.id),
          projectId,
          1,
        ),
      ),
    on,
  )
  useShortcut(
    'start.projectPrev',
    () =>
      setProjectId(
        cycle(
          (projects.data ?? []).map((p) => p.id),
          projectId,
          -1,
        ),
      ),
    on,
  )
  useShortcut(
    'start.agent',
    () => {
      setAutoAgent(false)
      setAgent((a) => (a === 'claude' ? 'codex' : 'claude'))
    },
    on,
  )
  useShortcut('start.model', () => document.getElementById('sp-model-input')?.focus(), on)

  const start = async () => {
    if (!machineId || !machine?.online || !prompt.trim() || accountBlocked) return
    if (!input.canSend)
      return toast('Remove or fix the attachments marked with an error first.', 'error')
    setBusy(true)
    setFailure(undefined)
    const runId = `r-${crypto.randomUUID().slice(0, 12)}`
    try {
      const pending = input.files
      if (pending.length) input.markUploading(true)
      const attachments = pending.length
        ? await b.uploadAttachments(pending, newSessionAttachmentScope(machineId), machineId)
        : undefined
      const sent = b.dispatch('run.start', machineId, {
        runId,
        agent,
        ...(accountId ? { accountId } : {}),
        prompt: prompt.trim(),
        ...(attachments ? { attachments } : {}),
        ...(model.trim() ? { model: model.trim() } : {}),
        ...(effort ? { effort } : {}),
        ...(projectId ? { projectId } : {}),
        ...(prefill?.workstreamId && projectId === prefill.projectId
          ? { workstreamId: prefill.workstreamId }
          : {}),
        ...(prefill?.taskId && projectId === prefill.projectId ? { taskId: prefill.taskId } : {}),
      })
      // The machine's answer (refusal included) must reach the user, never a silent "Starting…".
      let timer: ReturnType<typeof setTimeout> | undefined
      try {
        await Promise.race([
          sent,
          new Promise<never>((_, no) => {
            timer = setTimeout(() => no(new StartNoReply()), START_REPLY_MS)
          }),
        ])
      } finally {
        clearTimeout(timer)
      }
      input.clear()
      onClose()
      toast('Session started')
      void nav({ href: runHref(runId, projectId) }) // ia-shell: the run opens in its project's tab
    } catch (e) {
      if (e instanceof StorageNotConfigured) storage.off = true
      input.retryable()
      input.markUploading(false)
      if (
        e instanceof StartNoReply ||
        e instanceof CommandRejected ||
        e instanceof CommandTimeoutError
      )
        setFailure(startFailure(e, machine?.name ?? 'The machine'))
      else toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="New session"
      {...(prefill?.workstreamName ? { subtitle: prefill.workstreamName } : {})}
      placement="center"
      size="wide"
      shortcutScope="dialog:start"
    >
      <div className="dlg-body">
        {failure ? (
          <p role="alert" data-testid="start-failure" className="dlg-note">
            <AlertTriangle
              size={15}
              aria-hidden="true"
              className="mt-0.5 flex-none text-status-wait"
            />
            <span>
              {failure.title}
              {failure.fix ? <> {failure.fix}</> : null}
            </span>
          </p>
        ) : null}
        {noCheckout ? (
          <p role="status" className="dlg-note">
            <AlertTriangle
              size={15}
              aria-hidden="true"
              className="mt-0.5 flex-none text-status-wait"
            />
            <span>
              {project?.name} isn’t checked out on any machine yet. Open the project and choose Add
              machine.
            </span>
          </p>
        ) : null}
        <AttachmentDropZone input={input}>
          <label className="field">
            <span>What should the agent do?</span>
            <textarea
              className="input min-h-[128px] resize-y"
              rows={5}
              value={prompt}
              data-primary-input
              // biome-ignore lint/a11y/noAutofocus: primary input of the sheet
              autoFocus
              placeholder="Fix the flaky upload test and land it. Paste or drop screenshots and files to attach them."
              onChange={(e) => setPrompt(e.target.value)}
              onPaste={input.onPaste}
            />
          </label>
        </AttachmentDropZone>
        <div className="field">
          <span id="sp-project">
            Project <Kbd id="start.project" />
          </span>
          <div
            className="choice"
            role="radiogroup"
            aria-labelledby="sp-project"
            aria-keyshortcuts="Alt+P Alt+Shift+P"
          >
            {(projects.data ?? []).map((p) => (
              <button
                key={p.id}
                type="button"
                role="radio"
                aria-checked={p.id === projectId}
                onClick={() => setProjectId(p.id)}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <span id="sp-machine">Machine</span>
          <div className="choice" role="radiogroup" aria-labelledby="sp-machine">
            {allowed.map((m) => (
              <button
                key={m.id}
                type="button"
                role="radio"
                aria-checked={m.id === machineId}
                disabled={!m.online}
                onClick={() => setMachineId(m.id)}
              >
                {m.platform === 'mac' ? (
                  <Laptop size={14} aria-hidden="true" />
                ) : (
                  <Monitor size={14} aria-hidden="true" />
                )}
                {m.name}
                {m.online ? '' : ' (offline)'}
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <span id="sp-agent">
            Agent <Kbd id="start.agent" />
          </span>
          <div
            className="choice"
            role="radiogroup"
            aria-labelledby="sp-agent"
            aria-keyshortcuts="Alt+A"
          >
            {(['claude', 'codex'] as const).map((a) => (
              <button
                key={a}
                type="button"
                role="radio"
                aria-checked={a === agent}
                onClick={() => {
                  setAutoAgent(false)
                  setAgent(a)
                }}
              >
                <AgentGlyph agent={a} size={13} withName />
              </button>
            ))}
          </div>
          {autoAgent && autoRoute ? (
            <p className="m-0 text-[14px] text-text-3" data-testid="auto-route">
              Auto · {autoRoute.explanation}
            </p>
          ) : null}
        </div>
        <div className="dlg-pair">
          <div className="field">
            <span id="sp-model">
              Model <Kbd id="start.model" />
            </span>
            {agent === 'claude' ? (
              <select
                id="sp-model-input"
                aria-labelledby="sp-model"
                aria-keyshortcuts="Alt+M"
                className="input"
                value={model}
                onChange={(e) => setModel(e.target.value)}
              >
                <option value="">Agent default (recommended)</option>
                {pickerModels(agent).main.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
                {/* Older generations stay reachable, never the default. */}
                <optgroup label="Older models">
                  {pickerModels(agent).older.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label}
                    </option>
                  ))}
                </optgroup>
              </select>
            ) : (
              <input
                id="sp-model-input"
                aria-labelledby="sp-model"
                aria-keyshortcuts="Alt+M"
                className="input"
                value={model}
                placeholder="Agent default"
                spellCheck={false}
                autoCapitalize="none"
                onChange={(e) => setModel(e.target.value)}
              />
            )}
          </div>
          <div className="field">
            <span id="sp-effort">Effort</span>
            <select
              aria-labelledby="sp-effort"
              className="input"
              value={effort}
              onChange={(e) => setEffort(e.target.value)}
            >
              <option value="">Agent default</option>
              {effortsFor(agent).map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="field">
          <span>Provider account</span>
          {accounts ? (
            <select
              aria-label="Provider account"
              className="input"
              value={scopedSelection === AUTO_ACCOUNT ? AUTO_ACCOUNT : (accountId ?? '')}
              onChange={(e) => machineId && setSelection({ machineId, agent, id: e.target.value })}
            >
              {canAuto ? (
                <option value={AUTO_ACCOUNT}>Auto · most headroom, soonest reset</option>
              ) : null}
              {!account && scopedSelection !== AUTO_ACCOUNT ? (
                <option value={accountId ?? ''}>Choose an available account</option>
              ) : null}
              {accounts.map((a) => (
                <option key={a.id} value={a.id} disabled={!accountUsable(a)}>
                  {a.label} · {accountStatus(a)}
                </option>
              ))}
            </select>
          ) : (
            // The machine hasn't reported isolated accounts: runs use its existing CLI login.
            <select aria-label="Provider account" className="input" value="" disabled>
              <option value="">Existing CLI login (default)</option>
            </select>
          )}
          {auto ? (
            <span role="status" className="text-[14px] text-text-2">
              {auto.ok
                ? `Auto picked ${account?.label ?? auto.localId}: ${auto.explanation[0]?.replace(/^.*?: /, '') ?? ''}`
                : `Auto cannot start now: ${auto.explanation.at(-1) ?? ''}`}
            </span>
          ) : accountBlocked ? (
            <span role="status">
              This account is not ready. Choose another account or check readiness under You →
              Machines.
            </span>
          ) : null}
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          icon={<Play size={15} />}
          disabled={busy || !machine?.online || !prompt.trim() || accountBlocked}
          {...shortcutProps('start.submit', 'Start session')}
          onClick={() => void start()}
        >
          {busy ? 'Starting…' : 'Start session'}
          <Kbd id="start.submit" />
        </Button>
      </DialogFooter>
    </Sheet>
  )
}
